import { isIos } from "@repo/edtech-shared";

import { getKeyPressAction, getVideoClickAction } from "../../../lib";
import {
  ControlsActions,
  GlobalPlayerStore,
  KeyPressAction,
  SkipIndicator,
  VideoClickAction,
} from "../../types";

export const createControlsSlice = (store: GlobalPlayerStore): ControlsActions => {
  let controlsTimer: number | null = null;
  let skipTimer: number | null = null;
  let lastTapTs: number | null = null;

  const clearControlsTimer = () => {
    if (controlsTimer) {
      clearTimeout(controlsTimer);
      controlsTimer = null;
    }
  };

  const showControlsForLimitedTime = () => {
    store.set({
      isShowingControls: true,
    });
    clearControlsTimer();
    controlsTimer = window.setTimeout(() => {
      store.set({
        isShowingControls: false,
      });
    }, 3000);
  };

  const showControlsPermanently = () => {
    store.set({
      isShowingControls: true,
    });
    clearControlsTimer();
  };

  const resetControls = () => {
    store.set({
      isShowingControls: false,
      showSettings: false,
      isFullscreen: false,
      currentSkip: null,
    });
    clearControlsTimer();
    if (skipTimer) {
      clearTimeout(skipTimer); skipTimer = null;
    }
  };

  const toggleSettings = () => {
    const next = !store.get().showSettings;
    store.set({
      showSettings: next,
    });

    if (next) {
      showControlsPermanently();
    } else {
      showControlsForLimitedTime();
    }
  };

  const handleControlsPlayButtonClick = () => {
    store.playerActions.toggle();
    showControlsForLimitedTime();
  };

  const setFullscreen = (v: boolean) => store.set({
    isFullscreen: v,
  });

  const toggleFullscreen = () => {
    const videoEl = store.playerActions.getPlayerElement() as (HTMLVideoElement & {
      webkitEnterFullscreen?: () => void,
    }) | null;
    const wrap = store.get().playerWrapEl || videoEl?.parentElement || null;
    const isFs = store.get().isFullscreen;

    if (isIos() && videoEl?.webkitEnterFullscreen) {
      if (!isFs) videoEl.webkitEnterFullscreen();
      return;
    }

    if (!isFs) {
      wrap?.requestFullscreen?.();
      (wrap as any)?.mozRequestFullScreen?.();
      (wrap as any)?.msRequestFullscreen?.();
    } else {
      document.exitFullscreen?.();
      showControlsForLimitedTime();
    }
  };

  const showSkip = (side: SkipIndicator) => {
    const el = store.playerActions.getPlayerElement();
    if (!el) return;
    const delta = side === SkipIndicator.Next ? 15 : -15;
    el.currentTime = Math.max(0, el.currentTime + delta);

    store.set({
      currentSkip: side,
    });
    if (skipTimer) clearTimeout(skipTimer);
    skipTimer = window.setTimeout(() => store.set({
      currentSkip: null,
    }), 1000);
  };

  const onVideoClick = () => {
    const action = getVideoClickAction(store.get().isMobile);
    switch (action) {
      case VideoClickAction.TogglePlay:
        store.playerActions.toggle();
        showControlsForLimitedTime();
        break;
      case VideoClickAction.OnlyShowControls:
        showControlsForLimitedTime();
        break;
    }
  };

  const onVideoPointerMove = () => {
    showControlsForLimitedTime();
  };

  const onVideoPointerLeave = (e: PointerEvent) => {
    const wrap = store.get().playerWrapEl;
    const rel = e.relatedTarget as Node | null;

    if (wrap && rel && wrap.contains(rel)) {
      return;
    }

    const el = store.playerActions.getPlayerElement() as HTMLMediaElement | null;
    if (el?.paused) {
      showControlsPermanently();
    } else {
      showControlsForLimitedTime();
    }
  };

  const onVideoMobileTouch = (event: TouchEvent) => {
    const now = Date.now();
    const since = now - (lastTapTs || 0);
    const touchX = event.touches[ 0 ]?.clientX ?? 0;

    if (since < 300 && since > 0) {
      const screenWidth = (event.currentTarget as HTMLElement).offsetWidth;
      if (touchX < screenWidth / 2) showSkip(SkipIndicator.Previous);
      else showSkip(SkipIndicator.Next);
    }
    lastTapTs = now;
  };

  const onVideoKeyDown = (e: KeyboardEvent) => {
    const action = getKeyPressAction(e.key);
    switch (action) {
      case KeyPressAction.SkipPrevious:
        showSkip(SkipIndicator.Previous);
        break;
      case KeyPressAction.SkipNext:
        showSkip(SkipIndicator.Next);
        break;
      case KeyPressAction.TogglePlay:
        e.preventDefault();
        store.playerActions.toggle();
        showControlsForLimitedTime();
        break;
      case KeyPressAction.None:
        break;
    }
  };

  const bindVideoEvents = (video: HTMLMediaElement, onlyClick?: boolean) => {
    const onFsDoc = () => setFullscreen(!!document.fullscreenElement);
    const onBeginIOS = () => setFullscreen(true);
    const onEndIOS = () => {
      setFullscreen(false);
      showControlsForLimitedTime();
    };

    if (onlyClick) {
      video.addEventListener("click", store.playerActions.toggle);
      return () => {
        video.removeEventListener("click", store.playerActions.toggle);
      };
    }

    video.addEventListener("click", onVideoClick);
    video.addEventListener("pointermove", onVideoPointerMove);
    video.addEventListener("pointerleave", onVideoPointerLeave);
    video.addEventListener("touchstart", onVideoMobileTouch);
    // todo check this, mb need check player and then use on document or smth else
    video.addEventListener("keydown", onVideoKeyDown);
    document.addEventListener("fullscreenchange", onFsDoc);

    if (isIos()) {
      video.addEventListener("webkitbeginfullscreen", onBeginIOS);
      video.addEventListener("webkitendfullscreen", onEndIOS);
    }

    return () => {
      video.removeEventListener("click", onVideoClick);
      video.removeEventListener("pointermove", onVideoPointerMove);
      video.removeEventListener("pointerleave", onVideoPointerLeave);
      video.removeEventListener("touchstart", onVideoMobileTouch);
      video.removeEventListener("keydown", onVideoKeyDown);
      document.removeEventListener("fullscreenchange", onFsDoc);

      if (isIos()) {
        video.removeEventListener("webkitbeginfullscreen", onBeginIOS);
        video.removeEventListener("webkitendfullscreen", onEndIOS);
      }
    };
  };

  return {
    showControlsForLimitedTime,
    showControlsPermanently,
    clearControlsTimer,
    resetControls,
    toggleSettings,
    handleControlsPlayButtonClick,
    toggleFullscreen,
    setFullscreen,
    showSkip,
    onVideoClick,
    onVideoMobileTouch,
    onVideoKeyDown,
    bindVideoEvents,
    setPlayerWrapEl: (el) => store.set({
      playerWrapEl: el,
    }),
  };
};
